import { useLoaderData, useLocation, LoaderFunctionArgs } from "react-router-dom";
import { EVENT_REPORT } from "./fragment";
import { getColDefs, formatData } from "./helpers";
import { ReportFilters } from "../../components/ReportFilters";
import ReportHeader from "../../components/ReportHeader";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { organizationStore } from "../../../../store/organization";
import { userStore } from "../../../../store/user";
import { EventReportRow } from "./type";
import React, { useMemo, useRef, useState } from "react";
import { eventStore } from "../../../../store/event";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import { SecondaryButton, ToggleButton } from "../../../../components/Button";
import { AgGridReact } from "ag-grid-react";
import { CsvCell, GridApi, SizeColumnsToContentStrategy } from "ag-grid-community";
import { buildReportCsvHeader } from "../../utils/buildReportCsvHeader";
import { trackEvent, useEmptyStateTracking } from "../../../analytics";

const client = createApolloClient();
const { getState } = userStore;
const { getState: getOrganizationState } = organizationStore;
const { getState: getEventState } = eventStore;

export const eventReportLoader = async ({ request }: LoaderFunctionArgs) => {
  // RP-1937: no filter in the address (e.g. a sidebar click). Don't load every sale ever;
  // ReportFilters puts its dates back in the address and this loader runs again.
  if (!new URL(request.url).searchParams.has("where")) return [];
  const rawOrganizationId = getOrganizationState().organizationId;
  const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);
  const { eventId } = getEventState();
  const user = getState().user;

  const search = new URL(request.url);
  const where = search.searchParams.get("where");
  let whereVariable: any = where ? JSON.parse(where) : {};

  if (organizationId && organizationId !== 0) {
    whereVariable = {
      ...whereVariable,
      organization_id: {
        _eq: organizationId,
      },
    };
  }
  if (user?.vendor_id) {
    whereVariable = {
      ...whereVariable,
      vendor_id: {
        _eq: user?.vendor_id,
      },
    };
  }

  if (eventId) {
    whereVariable = {
      ...whereVariable,
      event_id: {
        _eq: eventId,
      },
    };
  } else if (user?.events) {
    whereVariable = {
      ...whereVariable,
      event_id: {
        _in: user?.events,
      },
    };
  }

  const { data } = await client.query({
    query: EVENT_REPORT,
    variables: { where: whereVariable },
  });
  return data.reports_financial || [];
};
const BarChartComponent = ({
  rows,
  selectedDataKey,
}: {
  rows: EventReportRow[];
  selectedDataKey: string;
}) => {
  if (!rows || rows?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full pb-4">
        <div className="text-gray-500 text-sm">No data to display</div>
      </div>
    );
  }
  const filteredData = rows.filter(item => item !== null && item !== undefined);
  const sortedData = filteredData.sort((a, b) => {
    const key = selectedDataKey as keyof EventReportRow;
    const aValue = a[key] as number;
    const bValue = b[key] as number;
    return bValue - aValue;
  });
  const limitedData = sortedData.slice(0, 20);
  const usdFormatter = (value: number) => {
    return `$${value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
  };
  function formatDataKey(key: string) {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
  const maxEventNameLength = Math.max(
    ...limitedData.map((item) => item.event_name?.length || 0)
  );
  const angleInRadians = (Math.abs(-30) * Math.PI) / 180;
  const textHeight = Math.sin(angleInRadians) * maxEventNameLength * 9.5;
  const xAxisHeight = textHeight > 50 ? textHeight : 50;

  return (
    <div className="flex bg-white rounded-md flex-row items-center w-full">
      <div className="overflow-x-auto">
        <div className="flex flex-col">
          <div className="inline-block min-w-full align-middle">
            <BarChart
              width={1200}
              height={350 + xAxisHeight}
              data={limitedData}
              margin={{ top: 20, right: 10, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="event_name"
                tick={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  fill: "#4a5568",
                }}
                {...(limitedData.length > 5 && {
                  interval: 0,
                  angle: -35,
                  textAnchor: "end",
                  height: xAxisHeight,
                })}
              />
              <YAxis
                width={100}
                tickFormatter={usdFormatter}
                tick={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  fill: "#4a5568",
                }}
              />
              <Tooltip
                contentStyle={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  backgroundColor: "#fff",
                  border: "1px solid #e2e8f0",
                  color: "#4a5568",
                }}
                formatter={usdFormatter}
              />
              <Bar
                dataKey={selectedDataKey}
                fill="#B71C1C"
                name={formatDataKey(selectedDataKey)}
              />
            </BarChart>
          </div>
        </div>
      </div>
    </div>
  );
};
export const EventReport = () => {
  const data = useLoaderData() as EventReportRow[];
  const location = useLocation();
  useEmptyStateTracking(data?.length ?? 0, { reason: "no_rows_returned" });
  const user = getState().user;
  const isAdmin = user?.role?.name === "admin";
  const colDefs = useMemo(() => getColDefs(isAdmin), [isAdmin]);
  const formattedData = formatData(data);
  const chartRows = formattedData.filter((row) => row.event_name !== "Total");
  const totalRow = formattedData.filter((row) => row.event_name === "Total");
  const tableRows = formattedData.filter((row) => row.event_name !== "Total");
  const [selectedDataKey, setSelectedDataKey] = useState("total_net_sales");
  const handleDataKeyChange = (newDataKey: string) => {
    setSelectedDataKey(newDataKey);
  };
  const gridRef = useRef<GridApi>();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const initialLocationsRef = useRef<{ label: string; value: string }[] | null>(null);
  if (!initialLocationsRef.current) {
    const uniqueLocationIds = [...new Set(data.map((row) => row.location_id))].filter((id) => !!id);

    initialLocationsRef.current = uniqueLocationIds
        .map((id) => {
          const match = data.find((row) => row.location_id === id);
          const locationName = match?.location_name || id.toString();
          return {
            label: locationName.toString(),
            value: id.toString(),
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
  }
  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `EventSalesReport_${timestamp}.csv`,
        columnSeparator: ",",
        allColumns: true,
        prependContent: buildReportCsvHeader({
          reportName: "Event Sales Report",
          searchString: location.search,
        }),
      };
      trackEvent("export", {
        payload: {
          format: "csv",
          row_count: tableRows.length,
          report_name: "Event Sales Report",
          search: location.search || null,
        },
      });
      gridRef.current.exportDataAsCsv(params);
    }
  };
  const gridOptions = {
    enableCellTextSelection: true,
  };
  return (
    <>
      <div className="bg-white px-4 pb-1 mb-3 rounded-md">
        <ReportHeader title="Event Sales Report" />
        <ReportFilters
          includeSubEvents
          includeDateOnly
          includeDates
          includeDatesStartOfYear
          includeVendors
          eventScopedVendors
          includeLocations
          locationOptions={initialLocationsRef.current}
          customQueryPaths={{
            dateFrom: "_and[0].transaction_date",
            dateTo: "_and[1].transaction_date",
          }}
        />
        <div className="flex">
          <div className="ml-auto">
            <SecondaryButton onClick={handleExportCSV}>
              Export to CSV
            </SecondaryButton>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "stretch", flexWrap: "wrap" }}>
        <ToggleButton
          isSelected={selectedDataKey === "total_net_sales"}
          onClick={() => handleDataKeyChange("total_net_sales")}
        >
          Total Net Sales
        </ToggleButton>
        <ToggleButton
          isSelected={selectedDataKey === "total_gross_sales"}
          onClick={() => handleDataKeyChange("total_gross_sales")}
        >
          Total Gross Sales
        </ToggleButton>
        <ToggleButton
          isSelected={selectedDataKey === "gross_cash_sales"}
          onClick={() => handleDataKeyChange("gross_cash_sales")}
        >
          Gross Cash Sales
        </ToggleButton>
        <ToggleButton
          isSelected={selectedDataKey === "gross_credit_sales"}
          onClick={() => handleDataKeyChange("gross_credit_sales")}
        >
          Gross Credit Sales
        </ToggleButton>
        <ToggleButton
          isSelected={selectedDataKey === "gross_rfid_sales"}
          onClick={() => handleDataKeyChange("gross_rfid_sales")}
        >
          Gross RFID Sales
        </ToggleButton>
        <ToggleButton
          isSelected={selectedDataKey === "gross_cash_balance_sales"}
          onClick={() => handleDataKeyChange("gross_cash_balance_sales")}
        >
          Gross Cash Balance Sales
        </ToggleButton>
        <ToggleButton
          isSelected={selectedDataKey === "gross_promo_balance_sales"}
          onClick={() => handleDataKeyChange("gross_promo_balance_sales")}
        >
          Gross Promo Balance Sales
        </ToggleButton>
        <ToggleButton
          isSelected={selectedDataKey === "gross_token_sales"}
          onClick={() => handleDataKeyChange("gross_token_sales")}
        >
          Gross Token Sales
        </ToggleButton>
      </div>
      <BarChartComponent rows={chartRows} selectedDataKey={selectedDataKey} />
      <div style={{ height: "60vh", marginTop: "4px" }}>
        <div
          className={"ag-theme-quartz"}
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact
            rowData={tableRows}
            columnDefs={colDefs}
            rowHeight={rowHeight}
            autoSizeStrategy={autoSizeStrategy}
            pagination={true}
            gridOptions={gridOptions}
            paginationPageSize={100}
            pinnedBottomRowData={totalRow}
            onGridReady={(params) => (gridRef.current = params.api)} // Set GridApi reference
          />
        </div>
      </div>
    </>
  );
};
